import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Lock, Fingerprint } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { clearToken, getToken } from "../../lib/auth";
import { inputCls, Field } from "../../components/forms/FormBits";
import useAdminNoindex from "../../hooks/useAdminNoindex";
import { assertPasskey, passkeySupported } from "../../lib/webauthn";

/* Server-authorised gate: the CEO CONTROL ROOM button renders only when /api/ceo/status
   (server-side is_owner) says is_primary_ceo. Step-up: passkey (preferred) or TOTP (fallback). */
export default function CeoGate() {
  useAdminNoindex();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [stepUp, setStepUp] = useState(false);
  const [useTotp, setUseTotp] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate("/partner-dashboard", { replace: true }); return; }
    api.get("/ceo/status").then(({ data }) => { setStatus(data); if (data.is_primary_ceo && data.ceo_session_active) navigate("/admin", { replace: true }); })
      .catch(() => { clearToken(); navigate("/partner-dashboard", { replace: true }); });
  }, [navigate]);

  const enter = (data) => { sessionStorage.setItem("ceo_csrf", data.csrf); toast.success("CEO authorization confirmed"); navigate("/admin", { replace: true }); };

  const verifyTotp = async (e) => {
    e.preventDefault(); setBusy(true);
    try { enter((await api.post("/ceo/step-up/totp", { code })).data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verifyPasskey = async () => {
    setBusy(true);
    try { enter(await assertPasskey("/ceo/step-up/webauthn/options", "/ceo/step-up/webauthn/verify")); }
    catch (err) { toast.error(err.response ? formatApiErrorDetail(err.response.data?.detail) : err.message); }
    finally { setBusy(false); }
  };

  const signOut = () => { clearToken(); navigate("/partner-dashboard", { replace: true }); };

  if (!status) return <div className="flex min-h-screen items-center justify-center bg-navy"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  if (!status.is_primary_ceo)
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy px-6 text-center" data-testid="ceo-access-denied">
        <div className="max-w-md rounded-2xl bg-white p-8"><Lock className="mx-auto h-8 w-8 text-red-600" /><h1 className="mt-3 font-serif text-2xl text-navy">ACCESS DENIED</h1>
          <p className="mt-2 text-sm text-muted-foreground">The CEO Control Room is reserved for the Primary CEO account.</p>
          <button onClick={signOut} className="mt-6 rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white" data-testid="ceo-denied-signout">Sign out</button></div>
      </div>
    );

  const passkeyReady = passkeySupported() && status.methods?.webauthn === "available";
  const showTotp = useTotp || !passkeyReady;
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-6" data-testid="ceo-gate">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <p className="overline text-xs text-gold">Primary CEO verified by server</p>
        {!stepUp ? (<>
          <h1 className="mt-2 font-serif text-2xl text-navy">Welcome, {status.display_name || "Amin"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">Entering the CEO Control Room requires step-up verification.</p>
          <button onClick={() => setStepUp(true)} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-6 py-3.5 text-sm font-bold tracking-wide text-white hover:bg-gold-dark" data-testid="ceo-control-room-btn"><ShieldCheck className="h-5 w-5" /> CEO CONTROL ROOM</button>
        </>) : (
          <div data-testid="ceo-step-up">
            <h1 className="mt-2 font-serif text-2xl text-navy">Step-up verification</h1>
            {passkeyReady && !useTotp && (<>
              <p className="mt-2 text-sm text-muted-foreground">Verify with your enrolled passkey (WebAuthn).</p>
              <button onClick={verifyPasskey} disabled={busy} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="ceo-passkey-verify">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} Verify with passkey
              </button>
              {status.mfa_enrolled && <button type="button" onClick={() => setUseTotp(true)} className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-navy" data-testid="ceo-use-totp">Use authenticator code instead</button>}
            </>)}
            {showTotp && (
              <form onSubmit={verifyTotp} data-testid="ceo-totp-form">
                <p className="mt-2 text-sm text-muted-foreground">{passkeyReady ? "Authenticator code (fallback)." : status.methods?.webauthn === "not_enrolled" ? "No passkey enrolled yet — enter your authenticator code (MFA). You can enrol a passkey inside the Control Room (Security)." : "Enter your authenticator code (MFA)."}</p>
                {!status.mfa_enrolled && <p className="mt-2 rounded bg-red-50 p-2 text-xs text-red-700" data-testid="ceo-mfa-required">MFA enrolment is required before CEO access. Enrol in Security settings first.</p>}
                <div className="mt-4"><Field label="Authenticator code" required><input inputMode="numeric" autoFocus className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} data-testid="ceo-totp-code" /></Field></div>
                <button disabled={busy || !status.mfa_enrolled} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="ceo-step-up-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify and enter</button>
                {passkeyReady && <button type="button" onClick={() => setUseTotp(false)} className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-navy" data-testid="ceo-use-passkey">Back to passkey</button>}
              </form>
            )}
          </div>
        )}
        <button onClick={signOut} className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-navy" data-testid="ceo-gate-signout">Sign out</button>
      </div>
    </div>
  );
}
